import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IStatefulEntity } from '../core/interfaces/entity-layer-manager.interface';
import { ISirena } from 'modelos/src';

/**
 * Estados posibles de una sirena (basado en SirenasService.devolverEstado)
 */
export type EstadoSirena = 'online' | 'offline' | 'encendida' | 'unknown';

/**
 * Interfaz extendida de sirena con información de ubicación y estado
 * (las sirenas en el código original vienen como MarkerMapa con data)
 */
export interface ISirenaExtendida {
  _id?: string;
  idCliente?: string;
  idExterno?: string;
  estado?: EstadoSirena; // Estado calculado (online/offline)
  coordenadas?: [number, number]; // Coordenadas para el mapa
  // Otros campos según necesidad
  [key: string]: any;
}

/**
 * SirenasLayerManager
 *
 * Manager para renderizar sirenas en el mapa.
 *
 * Características:
 * - Estados dinámicos (online/offline)
 * - Iconos según estado: verde (online), roja (offline), azul (unknown)
 * - Soporta clustering
 * - Color de cluster: negro (#000000)
 * - Implementa IStatefulEntity para manejo de estados
 *
 * Iconos:
 * - verde.png (online)
 * - roja.png (offline)
 * - azul.png (unknown/default)
 */
@Injectable({
  providedIn: 'root',
})
export class SirenasLayerManager
  extends BaseEntityLayerManager<ISirenaExtendida>
  implements IStatefulEntity<EstadoSirena>
{
  // ========== CONFIGURACIÓN ==========
  /**
   * Tipo de entidad para el manager
   *
   * IMPORTANTE: Este manager se usa para DOS tipos de popup:
   * - 'sirenas' → popup SIMPLE (solo "Tipo: sirena") en mapa general
   * - 'sirenasG' → popup DETALLADO (estado, chipId, etc.) en mapa específico de sirenas
   *
   * El tipo se determina por la clave usada en updateData():
   * - mapaFacilV2Service.updateData({ sirenas: [...] }) → popup simple
   * - mapaFacilV2Service.updateData({ sirenasG: [...] }) → popup detallado
   */
  entityType = 'sirenas';
  readonly supportsClustering = true;
  readonly clusterColor = '#d898b2';
  readonly zoomThreshold = 14; // Similar a OpenLayers: transición de círculos a iconos

  // IDs de layers (calculados dinámicamente según entityType)
  private get iconLayerId(): string {
    return `${this.entityType}-layer`;
  }
  private get circleLayerId(): string {
    return `${this.entityType}-circle-layer`;
  }
  private get unclusteredLayerId(): string {
    return `${this.entityType}-unclustered-layer`;
  }

  // Estado interno
  private currentZoom = 15; // Por defecto, mostrar iconos
  private mapaEspecifico = false; // Por defecto, usar pin fijo (mapa general)
  private zoomHandler?: () => void;

  // ========== MAPEO DE ESTADOS ==========
  //Mapeo de estados a íconos
  readonly stateToIconMap: Record<EstadoSirena, string> = {
    online: 'sirena-verde-icon',
    encendida: 'sirena-azul-icon',
    offline: 'sirena-naranja-icon',
    unknown: 'sirena-amarilla-icon',
  };

  // Mapeo de estados a colores (para círculos en zoom bajo)
  readonly stateToColorMap: Record<EstadoSirena, string> = {
    online: '#4CAF50', // Verde
    encendida: '#2196F3', // Azul
    offline: '#FF9800', // Naranja
    unknown: '#FFEB3B', // Amarillo
  };

  // Tamaños de visualización
  private readonly CIRCLE_RADIUS = 4; // Radio de círculos en zoom bajo (más visibles)
  private readonly ICON_SIZE_ESPECIFICO = 0.21; // Íconos específicos más grandes para mejor legibilidad
  private readonly ICON_SIZE_GENERAL = 0.05; // 800x800, contenido 512px → ~26px (igual a vehículos/alarmas)

  // ========== CONFIGURACIÓN DE TIPO DE MAPA ==========
  /**
   * Configura si el manager debe usar iconos por estado (mapa específico)
   * o un pin único (mapa general)
   */
  setMapaEspecifico(especifico: boolean): void {
    this.mapaEspecifico = especifico;

    // Si ya está inicializado, actualizar las expresiones de los layers
    if (this.isInitialized && this.map) {
      this.updateIconExpressions();
      this.updateClusterStyle();
    }
  }

  /**
   * Actualiza las expresiones de iconos al cambiar mapaEspecifico
   */
  private updateIconExpressions(): void {
    if (!this.map) return;

    // Actualizar currentZoom con el valor real del mapa
    this.currentZoom = this.map.getZoom();
    const iconExpression = this.getIconExpression();
    const iconSize = this.mapaEspecifico
      ? this.ICON_SIZE_ESPECIFICO
      : this.ICON_SIZE_GENERAL;
    // Mapa específico: pin gota (punta en la coordenada); mapa general: círculo centrado
    const iconAnchor = this.mapaEspecifico ? 'bottom' : 'center';

    for (const layerId of [this.iconLayerId, this.unclusteredLayerId]) {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'icon-image', iconExpression);
        this.map.setLayoutProperty(layerId, 'icon-size', iconSize);
        this.map.setLayoutProperty(layerId, 'icon-anchor', iconAnchor);
      }
    }
    // IMPORTANTE: Actualizar visibilidad según zoom actual solo si clustering OFF
    // Si clustering ON, los layers normales deben permanecer ocultos
    if (!this.clusteringEnabled) {
      this.updateZoomBasedVisibility();
    }
  }

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte una sirena a un Feature de GeoJSON
   */
  override entityToFeature(
    sirena: ISirenaExtendida,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(sirena);

    // Determinar estado
    const estado = this.determineEntityState(sirena);

    // Obtener coordenadas
    let coordinates: [number, number];

    if (sirena.coordenadas && sirena.coordenadas.length === 2) {
      coordinates = sirena.coordenadas;
    } else {
      coordinates = [-58.443832, -34.587386];
    }

    // Determinar qué datos pasar al popup
    let popupData;
    if ((sirena as any).popupData) {
      // Mapa de sirenas: ya viene con la estructura correcta
      popupData = (sirena as any).popupData;
    } else {
      // Mapa general: pasar el objeto completo
      popupData = sirena;
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: sirena._id,
        idExterno: sirena.idExterno,
        estado: estado,
        // Color para círculos (zoom bajo)
        color: this.stateToColorMap[estado],
        // Datos para el popup
        data: popupData,
      },
    };
  }

  /**
   * Determina el estado de una sirena
   * Basado en SirenasService.devolverEstado
   */
  determineEntityState(sirena: ISirenaExtendida): EstadoSirena {
    // Si viene con estado pre-calculado, usarlo
    if (sirena.estado) {
      const estadoNormalizado = String(sirena.estado).toLowerCase();
      if (
        estadoNormalizado === 'online' ||
        estadoNormalizado === 'offline' ||
        estadoNormalizado === 'encendida' ||
        estadoNormalizado === 'unknown'
      ) {
        return estadoNormalizado as EstadoSirena;
      }
    }

    // Lógica basada en el servicio original
    // Acceder a las propiedades anidadas si existen
    const errorActualizacion = (sirena as any).errorActualizacion;
    const actualizando = (sirena as any).actualizando;
    const sonidoEncendido = (sirena as any).sonidoEncendido;
    const luzEncendida = (sirena as any).luzEncendida;
    const online = (sirena as any).online;
    const activa = (sirena as any).activa;

    // Estados específicos primero
    if (online && !sonidoEncendido && !luzEncendida) {
      return 'online';
    }
    if (online === false) {
      return 'offline';
    }
    if (online && (sonidoEncendido || luzEncendida)) {
      return 'encendida';
    }
    return 'unknown';
  }

  /**
   * Expresión para el icono (dinámica según estado)
   */
  override getIconExpression(): string | mapboxgl.Expression {
    if (this.mapaEspecifico) {
      // Mapa específico: iconos dinámicos por estado
      const expression: mapboxgl.Expression = [
        'match',
        ['get', 'estado'],
        'online',
        this.stateToIconMap['online'],
        'encendida',
        this.stateToIconMap['encendida'],
        'offline',
        this.stateToIconMap['offline'],
        'unknown',
        this.stateToIconMap['unknown'],
        this.stateToIconMap['unknown'], // Default
      ];
      return expression;
    }

    // Mapa general: círculo estándar ('sirena-circulo-icon' usa assets/pins/circulo-sirena.png)

    return 'sirena-circulo-icon';
  }

  /**
   * Expresión para el color del círculo (en zoom bajo)
   */
  getCircleColorExpression(): mapboxgl.Expression {
    return [
      'match',
      ['get', 'estado'],
      'online',
      this.stateToColorMap['online'],
      'encendida',
      this.stateToColorMap['encendida'],
      'offline',
      this.stateToColorMap['offline'],
      // Default (requerido por Mapbox: último argumento)
      this.stateToColorMap['unknown'],
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea los layers para sirenas
   */
  protected override createLayers(): void {
    if (!this.map) return;

    this.currentZoom = this.map.getZoom();
    // 1. LAYERS DE CLUSTERING
    if (this.supportsClustering) {
      const clusterLayerId = `${this.entityType}-cluster-layer`;
      const clusterCountLayerId = `${this.entityType}-cluster-count-layer`;
      // Asegurar que el source clusterizado exista con clusterProperties adecuadas
      if (!this.map.getSource(this.clusteredSourceId)) {
        this.map.addSource(this.clusteredSourceId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          buffer: 0,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 40,
          // Cluster properties para evaluar prioridades de estado dentro del cluster
          clusterProperties: {
            has_online: ['any', ['==', ['get', 'estado'], 'online']],
            has_encendida: ['any', ['==', ['get', 'estado'], 'encendida']],
            has_offline: ['any', ['==', ['get', 'estado'], 'offline']],
            has_unknown: ['any', ['==', ['get', 'estado'], 'unknown']],
          },
        } as any);
      }

      // Expresión de color para clusters
      const clusterColorExpression = this.buildClusterColorExpression();

      // Layer de círculos del cluster con color dinámico por prioridad
      this.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: this.clusteredSourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': clusterColorExpression as any,
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 50, 40],
          'circle-opacity': 0.7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Layer de texto del cluster
      this.addLayer({
        id: clusterCountLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Unclustered layer
      this.addLayer({
        id: this.unclusteredLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': this.mapaEspecifico
            ? this.ICON_SIZE_ESPECIFICO
            : this.ICON_SIZE_GENERAL,
          'icon-allow-overlap': true,
          'icon-padding': 0,
          // Mapa específico: pin gota (punta en la coordenada); mapa general: círculo centrado
          'icon-anchor': this.mapaEspecifico ? 'bottom' : 'center',
          visibility: 'none',
        },
      });
    }

    // 2. ICON LAYER (sirenas sin clustering)
    // En mapa general (mapaEspecifico=false): siempre visible
    // En mapa específico (mapaEspecifico=true): visible solo si zoom >= 14
    this.addLayer({
      id: this.iconLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': this.mapaEspecifico
          ? this.ICON_SIZE_ESPECIFICO
          : this.ICON_SIZE_GENERAL,
        visibility: this.mapaEspecifico
          ? this.currentZoom >= this.zoomThreshold
            ? 'visible'
            : 'none'
          : 'visible', // Mapa general: siempre visible
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
        // Mapa específico: pin gota (punta en la coordenada); mapa general: círculo centrado
        'icon-anchor': this.mapaEspecifico ? 'bottom' : 'center',
      },
    });

    // 3. CIRCLE LAYER (zoom < threshold)
    // Solo se usa en mapa específico; en mapa general permanece oculto
    this.addLayer({
      id: this.circleLayerId,
      type: 'circle',
      source: this.sourceId,
      paint: {
        'circle-radius': this.CIRCLE_RADIUS,
        'circle-color': this.getCircleColorExpression(),
        'circle-opacity': 0.8,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
      layout: {
        visibility: this.mapaEspecifico
          ? this.currentZoom < this.zoomThreshold
            ? 'visible'
            : 'none'
          : 'none',
      },
    });

    // Listener de zoom
    this.setupZoomListener();
  }

  /**
   * Construye la expresión de color de clusters según el modo de mapa
   * - Mapa específico: prioridad por estado, Ok/Encendida en VERDE
   * - Mapa general: SIEMPRE amarillo (evita naranjas por datos incompletos)
   */
  private buildClusterColorExpression(): mapboxgl.Expression | string {
    if (!this.mapaEspecifico) {
      // Mapa general → constante amarillo
      return this.clusterColor as string;
    }

    // Mapa específico → colores por prioridad
    return [
      'case',
      ['get', 'has_online'],
      'rgb(76, 175, 80)',
      ['get', 'has_encendida'],
      'rgb(33, 150, 243)',
      ['get', 'has_offline'],
      'rgb(255, 152, 0)',
      ['get', 'has_unknown'],
      'rgb(255, 235, 59)',
      this.clusterColor,
    ] as any;
  }

  /**
   * Actualiza el estilo (color) del layer de cluster cuando cambia mapaEspecifico
   */
  private updateClusterStyle(): void {
    if (!this.map) return;
    const clusterLayerId = `${this.entityType}-cluster-layer`;
    if (this.map.getLayer(clusterLayerId)) {
      this.map.setPaintProperty(
        clusterLayerId,
        'circle-color',
        this.buildClusterColorExpression() as any,
      );
    }
  }

  /**
   * Override: crear sources para incluir clusterProperties en clusteredSource
   */
  protected override createSources(): void {
    if (!this.map) return;

    // Source normal (sin clustering)
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        buffer: 0,
      });
    }

    // Source clustered con propiedades por estado
    if (
      this.supportsClustering &&
      !this.map.getSource(this.clusteredSourceId)
    ) {
      this.map.addSource(this.clusteredSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        buffer: 0,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 40,
        clusterProperties: {
          has_online: ['any', ['==', ['get', 'estado'], 'online']],
          has_encendida: ['any', ['==', ['get', 'estado'], 'encendida']],
          has_offline: ['any', ['==', ['get', 'estado'], 'offline']],
          has_unknown: ['any', ['==', ['get', 'estado'], 'unknown']],
        },
      } as any);
    }
  }

  // ========== ZOOM AWARENESS ==========

  private setupZoomListener(): void {
    if (!this.map) return;

    this.zoomHandler = () => {
      if (!this.map) return;
      const newZoom = this.map.getZoom();
      this.onZoomChange(newZoom);
    };

    // OPTIMIZACIÓN: Usar 'zoomend' en lugar de 'zoom' para evitar ejecutar en cada frame
    // 'zoom' se dispara 60 veces/seg → causa jank
    // 'zoomend' se dispara solo cuando termina el zoom → smooth
    this.map.on('zoomend', this.zoomHandler);
  }

  /**
   * Handler para cambios de zoom (implementa IZoomAware)
   */
  private onZoomChange(zoom: number): void {
    const previousZoom = this.currentZoom;
    this.currentZoom = zoom;

    // Si cruzamos el threshold, cambiar visibilidad de layers
    const previousMode = previousZoom >= this.zoomThreshold;
    const currentMode = zoom >= this.zoomThreshold;

    if (previousMode !== currentMode) {
      // IMPORTANTE: Solo actualizar visibilidad basada en zoom si clustering está OFF
      // Cuando clustering ON, los layers normales (iconLayerId/circleLayerId) deben permanecer ocultos
      if (!this.clusteringEnabled) {
        this.updateZoomBasedVisibility();
      }
    }
  }
  /**
   * Actualiza la visibilidad de layers según el zoom
   * Solo aplica en mapa específico (mapaEspecifico=true)
   * En mapa general (mapaEspecifico=false), siempre muestra iconos
   * IMPORTANTE: Respeta this.isVisible para no mostrar layers cuando el filtro está apagado
   */
  private updateZoomBasedVisibility(): void {
    if (!this.map) return;

    if (this.mapaEspecifico) {
      // Mapa específico: alternar entre iconos y círculos según zoom (respetando filtro)
      const showIcons = this.currentZoom >= this.zoomThreshold;

      this.setLayerVisibility(this.iconLayerId, this.isVisible && showIcons);
      this.setLayerVisibility(this.circleLayerId, this.isVisible && !showIcons);
    } else {
      // Mapa general: siempre iconos, nunca círculos (respetando filtro)
      this.setLayerVisibility(this.iconLayerId, this.isVisible);
      this.setLayerVisibility(this.circleLayerId, false);
    }
  }

  // ========== CLUSTERING ==========

  /**
   * Maneja la visibilidad según clustering
   */
  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      // CLUSTERING ON (respetando filtro)
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(this.unclusteredLayerId, this.isVisible);
      this.setLayerVisibility(this.iconLayerId, false);
      this.setLayerVisibility(this.circleLayerId, false);
    } else {
      // CLUSTERING OFF
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredLayerId, false);
      // Restaurar visibilidad según zoom (respetando filtro)
      // IMPORTANTE: updateZoomBasedVisibility ya respeta this.isVisible internamente
      this.updateZoomBasedVisibility();
    }
  }

  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }
  // ========== CLEANUP ==========
  /**
   * Override de cleanup para remover event listeners
   */
  override cleanup(): void {
    // Remover zoom listener
    if (this.map && this.zoomHandler) {
      this.map.off('zoomend', this.zoomHandler);
    }

    // Resetear estados específicos de sirenas
    this.currentZoom = 15;
    this.mapaEspecifico = false; // Resetear a false para mapa general

    // Llamar al cleanup base
    super.cleanup();
  }

  // ========== VISIBILIDAD GENERAL ==========
  /**
   * Override de setVisibility
   */
  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredLayerId, visible);
    } else {
      if (this.mapaEspecifico) {
        // Mapa específico: según zoom, mostrar iconos o círculos
        if (this.currentZoom >= this.zoomThreshold) {
          this.setLayerVisibility(this.iconLayerId, visible);
          this.setLayerVisibility(this.circleLayerId, false);
        } else {
          this.setLayerVisibility(this.iconLayerId, false);
          this.setLayerVisibility(this.circleLayerId, visible);
        }
      } else {
        // Mapa general: siempre iconos, nunca círculos
        this.setLayerVisibility(this.iconLayerId, visible);
        this.setLayerVisibility(this.circleLayerId, false);
      }
    }
  }
}
